<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useOrgStore } from '../stores/orgStore.js'
import { useUsersStore } from '../stores/users.js'
import {
  createLotteryPrizeRequest,
  deleteLotteryPrizeRequest,
  downloadWinnersCsvRequest,
  drawWinnerRequest,
  fetchDrawHistoryRequest,
  fetchLotteryPrizesRequest,
  fetchLotteryReportRequest,
  fetchLotteryWinnersRequest,
  publishLotteryWinnersRequest,
  updateLotteryPrizeRequest,
} from '../services/lotteryService.js'

const props = defineProps({
  lotteryId: {
    type: [String, Number],
    required: true,
  },
  role: {
    type: String,
    default: '',
  },
  isSuperAdmin: {
    type: Boolean,
    default: false,
  },
})

const orgStore = useOrgStore()
const usersStore = useUsersStore()

const role = computed(() => String(props.role || '').toLowerCase())
const isAdminRole = computed(() => props.isSuperAdmin || role.value === 'admin')
const isManagerRole = computed(() => role.value === 'manager')
const canManage = computed(() => isAdminRole.value || isManagerRole.value)

const error = ref('')
const globalMessage = ref('')

const drawLoading = ref(false)
const drawHistoryLoading = ref(false)
const drawHistory = ref([])
const lastDrawResult = ref(null)

const prizesLoading = ref(false)
const prizes = ref([])
const createPrizeLoading = ref(false)
const createPrizeForm = reactive({
  name: '',
  value: '',
  rank: '',
  donor_name: '',
})
const prizeEditById = reactive({})
const savingPrizeId = ref(null)
const deletingPrizeId = ref(null)

const reportLoading = ref(false)
const reportError = ref('')
const reportData = ref(null)
const reportTab = ref('summary')
const reportSnapshotId = ref('')

const exportLoading = ref(false)

const winnersLoading = ref(false)
const winners = ref([])
const publishLoading = ref(false)

const getOrgId = () => orgStore.activeOrgId

const parseError = (err, fallback) => {
  const detail = err?.response?.data?.detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const messages = detail.map((item) => item?.msg).filter(Boolean)
    if (messages.length > 0) return messages.join(', ')
  }
  return fallback
}

const normalizeRows = (payload) => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.results)) return payload.results
  if (Array.isArray(payload?.data)) return payload.data
  return []
}

const toPrizePayload = (source) => ({
  name: String(source?.name || '').trim(),
  value: Number(source?.value || 0),
  rank: Number(source?.rank || 0),
  donor_name: String(source?.donor_name || '').trim() || null,
})

const getPrizeId = (prize) => Number(prize?.id ?? prize?.prize_id)
const getWinnerId = (winner) => Number(winner?.id ?? winner?.winner_id)

const winnerIdentityLabel = (winner) => {
  const userId = Number(winner?.winner_user_id ?? winner?.user_id)
  const fallbackUsername = winner?.username ?? winner?.winner_username ?? ''
  if (typeof usersStore.resolveUserIdentity === 'function') {
    const identity = usersStore.resolveUserIdentity(userId, fallbackUsername)
    const base = identity.displayName || identity.username || (Number.isInteger(userId) && userId > 0 ? `User #${userId}` : 'Unknown user')
    return identity.phone ? `${base} (${identity.phone})` : base
  }
  if (fallbackUsername) return String(fallbackUsername)
  return Number.isInteger(userId) && userId > 0 ? `User #${userId}` : 'Unknown user'
}

const readDrawFields = (row) => {
  if (!row || typeof row !== 'object') return null
  return {
    snapshot_id: row.snapshot_id ?? row.snapshotId ?? row.id ?? 'N/A',
    rng_seed: row.rng_seed ?? row.rngSeed ?? 'N/A',
    draw_reference: row.draw_reference ?? row.drawReference ?? row.reference ?? 'N/A',
    created_at: row.created_at ?? row.executed_at ?? row.timestamp ?? '',
  }
}

const resetFeedback = () => {
  error.value = ''
  globalMessage.value = ''
}

const loadDrawHistory = async () => {
  drawHistoryLoading.value = true
  try {
    const response = await fetchDrawHistoryRequest(props.lotteryId, getOrgId())
    drawHistory.value = normalizeRows(response)
    const latest = drawHistory.value[0] || null
    lastDrawResult.value = readDrawFields(latest)
  } catch (err) {
    console.error('[lottery-api-ops] draw history failed', err)
    error.value = parseError(err, 'Failed to load draw history.')
    drawHistory.value = []
    lastDrawResult.value = null
  } finally {
    drawHistoryLoading.value = false
  }
}

const drawNow = async () => {
  if (!canManage.value || drawLoading.value) return
  resetFeedback()
  drawLoading.value = true
  try {
    const result = await drawWinnerRequest(props.lotteryId, getOrgId())
    lastDrawResult.value = readDrawFields(result)
    globalMessage.value = 'Draw completed.'
    await Promise.all([loadDrawHistory(), loadReport(), loadWinners()])
  } catch (err) {
    console.error('[lottery-api-ops] draw failed', err)
    error.value = parseError(err, 'Draw failed.')
  } finally {
    drawLoading.value = false
  }
}

const loadPrizes = async () => {
  prizesLoading.value = true
  try {
    const response = await fetchLotteryPrizesRequest(props.lotteryId, getOrgId())
    prizes.value = normalizeRows(response)
  } catch (err) {
    console.error('[lottery-api-ops] prizes load failed', err)
    error.value = parseError(err, 'Failed to load prizes.')
    prizes.value = []
  } finally {
    prizesLoading.value = false
  }
}

const startPrizeEdit = (prize) => {
  const id = getPrizeId(prize)
  if (!id) return
  prizeEditById[id] = {
    name: prize?.name ?? '',
    value: prize?.value ?? 0,
    rank: prize?.rank ?? 0,
    donor_name: prize?.donor_name ?? '',
  }
}

const cancelPrizeEdit = (id) => {
  delete prizeEditById[id]
}

const savePrizeEdit = async (prizeId) => {
  if (!canManage.value || savingPrizeId.value) return
  const draft = prizeEditById[prizeId]
  if (!draft) return

  savingPrizeId.value = Number(prizeId)
  resetFeedback()
  try {
    await updateLotteryPrizeRequest(prizeId, toPrizePayload(draft), getOrgId())
    globalMessage.value = 'Prize updated.'
    delete prizeEditById[prizeId]
    await Promise.all([loadPrizes(), loadReport()])
  } catch (err) {
    console.error('[lottery-api-ops] prize update failed', err)
    error.value = parseError(err, 'Failed to update prize.')
  } finally {
    savingPrizeId.value = null
  }
}

const createPrize = async () => {
  if (!canManage.value || createPrizeLoading.value) return

  createPrizeLoading.value = true
  resetFeedback()
  try {
    await createLotteryPrizeRequest(props.lotteryId, toPrizePayload(createPrizeForm), getOrgId())
    createPrizeForm.name = ''
    createPrizeForm.value = ''
    createPrizeForm.rank = ''
    createPrizeForm.donor_name = ''
    globalMessage.value = 'Prize created.'
    await Promise.all([loadPrizes(), loadReport()])
  } catch (err) {
    console.error('[lottery-api-ops] prize create failed', err)
    error.value = parseError(err, 'Failed to create prize.')
  } finally {
    createPrizeLoading.value = false
  }
}

const deletePrize = async (prizeId) => {
  if (!canManage.value || deletingPrizeId.value) return

  deletingPrizeId.value = Number(prizeId)
  resetFeedback()
  try {
    await deleteLotteryPrizeRequest(prizeId, getOrgId())
    globalMessage.value = 'Prize deleted.'
    await Promise.all([loadPrizes(), loadReport()])
  } catch (err) {
    console.error('[lottery-api-ops] prize delete failed', err)
    error.value = parseError(err, 'Failed to delete prize.')
  } finally {
    deletingPrizeId.value = null
  }
}

const loadReport = async () => {
  reportLoading.value = true
  reportError.value = ''
  try {
    const snapshot = String(reportSnapshotId.value || '').trim()
    reportData.value = await fetchLotteryReportRequest(props.lotteryId, snapshot || null, getOrgId())
  } catch (err) {
    console.error('[lottery-api-ops] report load failed', err)
    reportData.value = null
    reportError.value = parseError(err, 'Failed to load report.')
  } finally {
    reportLoading.value = false
  }
}

const reportSummary = computed(() => reportData.value?.summary || reportData.value?.report_summary || null)
const reportPrizes = computed(() => normalizeRows(reportData.value?.prizes || reportData.value?.prize_rows || reportData.value?.prize_items))
const reportWinners = computed(() => normalizeRows(reportData.value?.winners || reportData.value?.winner_rows || reportData.value?.winner_items))
const reportSnapshot = computed(() => reportData.value?.snapshot || reportData.value?.snapshot_info || reportData.value?.metadata || null)

const downloadWinnersCsv = async () => {
  if (exportLoading.value) return

  exportLoading.value = true
  resetFeedback()
  try {
    const blob = await downloadWinnersCsvRequest(props.lotteryId, getOrgId())
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `lottery-${props.lotteryId}-winners.csv`
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
    globalMessage.value = 'CSV download started.'
  } catch (err) {
    console.error('[lottery-api-ops] csv export failed', err)
    error.value = parseError(err, 'Failed to download winners CSV.')
  } finally {
    exportLoading.value = false
  }
}

const loadWinners = async () => {
  winnersLoading.value = true
  try {
    const response = await fetchLotteryWinnersRequest(props.lotteryId, getOrgId())
    winners.value = normalizeRows(response)
  } catch (err) {
    console.error('[lottery-api-ops] winners load failed', err)
    error.value = parseError(err, 'Failed to load winners.')
    winners.value = []
  } finally {
    winnersLoading.value = false
  }
}

const publishWinners = async () => {
  if (!canManage.value || publishLoading.value) return

  publishLoading.value = true
  resetFeedback()
  try {
    await publishLotteryWinnersRequest(props.lotteryId, getOrgId())
    globalMessage.value = 'Winners published.'
    await loadWinners()
  } catch (err) {
    console.error('[lottery-api-ops] winner publish failed', err)
    error.value = parseError(err, 'Failed to publish winners.')
  } finally {
    publishLoading.value = false
  }
}

onMounted(async () => {
  await usersStore.ensureUsersHydrated()
  await Promise.all([loadDrawHistory(), loadPrizes(), loadReport(), loadWinners()])
})
</script>

<template>
  <section class="api-ops-panel">
    <h2>Lottery API Operations</h2>

    <p v-if="globalMessage" class="success">{{ globalMessage }}</p>
    <p v-if="error" class="error">{{ error }}</p>

    <section class="panel" v-if="canManage">
      <h3>Draw Control</h3>
      <button class="btn" type="button" :disabled="drawLoading" @click="drawNow">
        {{ drawLoading ? 'Drawing...' : 'Draw Now' }}
      </button>
      <p v-if="drawHistoryLoading" class="muted">Loading draw history...</p>
      <div v-if="lastDrawResult" class="grid">
        <p><strong>snapshot_id:</strong> {{ lastDrawResult.snapshot_id }}</p>
        <p><strong>rng_seed:</strong> {{ lastDrawResult.rng_seed }}</p>
        <p><strong>draw_reference:</strong> {{ lastDrawResult.draw_reference }}</p>
      </div>
      <p v-else class="muted">No draw result found yet.</p>
    </section>

    <section class="panel" v-if="canManage">
      <h3>Prizes</h3>
      <p v-if="prizesLoading" class="muted">Loading prizes...</p>
      <table v-else-if="prizes.length" class="table">
        <thead>
          <tr>
            <th>name</th>
            <th>value</th>
            <th>rank</th>
            <th>donor_name</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="prize in prizes" :key="getPrizeId(prize)">
            <template v-if="prizeEditById[getPrizeId(prize)]">
              <td><input v-model="prizeEditById[getPrizeId(prize)].name" class="input" /></td>
              <td><input v-model="prizeEditById[getPrizeId(prize)].value" class="input" type="number" /></td>
              <td><input v-model="prizeEditById[getPrizeId(prize)].rank" class="input" type="number" /></td>
              <td><input v-model="prizeEditById[getPrizeId(prize)].donor_name" class="input" /></td>
              <td>
                <button class="btn" type="button" :disabled="savingPrizeId === getPrizeId(prize)" @click="savePrizeEdit(getPrizeId(prize))">Save</button>
                <button class="btn-secondary" type="button" :disabled="savingPrizeId === getPrizeId(prize)" @click="cancelPrizeEdit(getPrizeId(prize))">Cancel</button>
              </td>
            </template>
            <template v-else>
              <td>{{ prize.name }}</td>
              <td>{{ prize.value }}</td>
              <td>{{ prize.rank }}</td>
              <td>{{ prize.donor_name || '-' }}</td>
              <td>
                <button class="btn" type="button" @click="startPrizeEdit(prize)">Edit</button>
                <button class="btn-danger" type="button" :disabled="deletingPrizeId === getPrizeId(prize)" @click="deletePrize(getPrizeId(prize))">
                  {{ deletingPrizeId === getPrizeId(prize) ? 'Deleting...' : 'Delete' }}
                </button>
              </td>
            </template>
          </tr>
        </tbody>
      </table>
      <p v-else class="muted">No prizes found.</p>

      <div class="create-grid">
        <input v-model="createPrizeForm.name" class="input" placeholder="name" />
        <input v-model="createPrizeForm.value" class="input" type="number" placeholder="value" />
        <input v-model="createPrizeForm.rank" class="input" type="number" placeholder="rank" />
        <input v-model="createPrizeForm.donor_name" class="input" placeholder="donor_name" />
        <button class="btn" type="button" :disabled="createPrizeLoading" @click="createPrize">
          {{ createPrizeLoading ? 'Creating...' : 'Add Prize' }}
        </button>
      </div>
    </section>

    <section class="panel">
      <h3>Report Viewer</h3>
      <div class="toolbar">
        <input v-model="reportSnapshotId" class="input" placeholder="snapshot_id (optional)" />
        <button class="btn" type="button" :disabled="reportLoading" @click="loadReport">Load Report</button>
        <button class="btn-secondary" type="button" :disabled="exportLoading" @click="downloadWinnersCsv">
          {{ exportLoading ? 'Downloading...' : 'Download Winners CSV' }}
        </button>
      </div>
      <p v-if="reportError" class="error">{{ reportError }}</p>
      <p v-if="reportLoading" class="muted">Loading report...</p>

      <template v-if="!reportLoading && reportData">
        <div class="tabs">
          <button class="tab" :class="{ active: reportTab === 'summary' }" @click="reportTab = 'summary'">Summary</button>
          <button class="tab" :class="{ active: reportTab === 'prizes' }" @click="reportTab = 'prizes'">Prizes</button>
          <button class="tab" :class="{ active: reportTab === 'winners' }" @click="reportTab = 'winners'">Winners</button>
          <button class="tab" :class="{ active: reportTab === 'snapshot' }" @click="reportTab = 'snapshot'">Snapshot info</button>
        </div>

        <pre v-if="reportTab === 'summary'" class="json-block">{{ JSON.stringify(reportSummary, null, 2) }}</pre>
        <pre v-else-if="reportTab === 'prizes'" class="json-block">{{ JSON.stringify(reportPrizes, null, 2) }}</pre>
        <pre v-else-if="reportTab === 'winners'" class="json-block">{{ JSON.stringify(reportWinners, null, 2) }}</pre>
        <pre v-else class="json-block">{{ JSON.stringify(reportSnapshot, null, 2) }}</pre>
      </template>
    </section>

    <section class="panel" v-if="canManage">
      <h3>Winners</h3>
      <div class="toolbar">
        <button class="btn" type="button" :disabled="winnersLoading" @click="loadWinners">Refresh winners</button>
        <button class="btn-warning" type="button" :disabled="publishLoading" @click="publishWinners">
          {{ publishLoading ? 'Publishing...' : 'Publish Winners' }}
        </button>
      </div>

      <p v-if="winnersLoading" class="muted">Loading winners...</p>
      <table v-else-if="winners.length" class="table">
        <thead>
          <tr>
            <th>ticket_number</th>
            <th>prize_name</th>
            <th>prize_rank</th>
            <th>winner</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="winner in winners" :key="getWinnerId(winner)">
            <td>{{ winner.ticket_number ?? winner.ticket ?? '-' }}</td>
            <td>{{ winner.prize_name ?? winner.prize?.name ?? '-' }}</td>
            <td>{{ winner.prize_rank ?? winner.rank ?? '-' }}</td>
            <td>{{ winnerIdentityLabel(winner) }}</td>
          </tr>
        </tbody>
      </table>
      <p v-else class="muted">No winners found.</p>
    </section>
  </section>
</template>

<style scoped>
.api-ops-panel {
  display: grid;
  gap: 1rem;
}

.panel {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 1rem;
}

.grid {
  display: grid;
  gap: 0.25rem;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th,
.table td {
  border: 1px solid #e5e7eb;
  padding: 0.45rem;
  text-align: left;
}

.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
}

.tab {
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  border-radius: 6px;
  padding: 0.3rem 0.6rem;
}

.tab.active {
  background: #dbeafe;
  border-color: #93c5fd;
}

.json-block {
  max-height: 280px;
  overflow: auto;
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 8px;
  padding: 0.65rem;
}

.create-grid {
  margin-top: 0.75rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.5rem;
}

.input {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.35rem 0.5rem;
}

.btn,
.btn-secondary,
.btn-danger,
.btn-warning {
  border: none;
  border-radius: 6px;
  padding: 0.35rem 0.6rem;
  color: #fff;
}

.btn {
  background: #2563eb;
}

.btn-secondary {
  background: #4b5563;
}

.btn-danger {
  background: #b91c1c;
}

.btn-warning {
  background: #92400e;
}

.success {
  color: #166534;
}

.error {
  color: #b91c1c;
}

.muted {
  color: #6b7280;
}
</style>



